import React, {
  ReactElement, useEffect, useRef, useState, useCallback, Ref,
} from 'react';
import {
  StyleSheet, Text, View, ScrollView, Platform, TextInput, NativeEventEmitter, FlatList, StatusBar,
  BackHandler, Pressable,
} from 'react-native';
import Modal from 'react-native-modal';
import Moment from 'moment';
import * as Application from 'expo-application';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Animated, { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import * as Device from 'expo-device';
import { useIsFocused } from '@react-navigation/native';
import * as Notifications from 'expo-notifications';
import SearchIcon from '../../components/SearchIcon';
import Colors from '../../constants/Colors';
import { RootTabScreenProps } from '../../types';
import LeftArrow from '../../components/LeftArrow';
import getTickets from '../../requests/Tickets/GetTickets';
import Style from '../../constants/Style';
import deleteTicket from '../../requests/Tickets/DeleteTicket';
import getUsers from '../../requests/Users/GetUsers';
import getAssets from '../../requests/Assets/GetAssets';
import FilterIcon16 from '../../components/16pxIcons/FilterIcon';
import getUserGroups from '../../requests/Users/GetUserGroups';
import AlertModal from '../../components/AlertModal';
import TicketsFilter from './components/TicketsFilter';
import CloseModalIcon from '../../components/CloseModalIcon';
import isMobile from '../../components/IsMobile';
import TicketList from './components/TicketList';
import TicketFilterTagSection from './components/TicketFilterTagSection';
import SelectedTicket from './components/SelectedTicket';
import TicketSharePane from './components/TicketSharePane';
import TicketOptions from './components/TicketOptions';
import WelcomeModal from '../../components/Profile/WelcomeModal';
import { DateRange, DateSelectionRange } from '../../redux/dateRangeSlice';
import { logEvent } from '../../components/LogEvent';
import getPushNotificationsToken from '../../components/NotificationHelper';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import NoTicketsImage from './components/NoTicketsImage';
import {
  TicketsFilterState, resetAllTicketFilters, updateTicketsFilterAssetId,
} from '../../redux/ticketsFilterSlice';
import getMyUser from '../../requests/Users/GetMyUser';
import PressableStyled from '../../components/PressableStyled';
import getSupportedVersions from '../../requests/SignIn/GetSupportedVersions';
import UpdateAppModal from '../../components/UpdateAppModal';
import {
  AssetInstance, UserPermission, Ticket, TicketStatus, UserInstance, UserStatus,
} from '../../constants/Types';
import { SelectedTicketState, updateSelectedTicket } from '../../redux/selectedTicketSlice';
import {
  DELETE_TICKET_SUCCESS, noPermissionsSubtitle, noPermissionsTitle, TICKET_LIST_LOAD_FAILURE,
} from '../../constants/AlertMessages';
import { updateBuildingId } from '../../redux/selectedBuildingSlice';
import { updateUsername } from '../../redux/userSlice';
import TicketCountWidget, {
  OpenTicketsShown, OpenTicketStatistics,
} from './components/TicketCountWidget';
import getTicket from '../../requests/Tickets/GetTicket';
import NoBuildingsIconMobile from '../../components/EmptyState/NoBuildingsIconMobile';
import SearchBar from '../../components/SearchBar';
import { assetName } from './data';
import Button from '../../components/Button';
import { Building } from '../LocationManagement/components/AddBuildingModal';
import { BuildingSettingsPriority } from '../LocationManagement/components/BuildingSettingsModal/data';

export default function TicketsScreen({ route, navigation }: RootTabScreenProps<'Tickets'>) {
  const selectedTicketDetails: SelectedTicketState = useSelector((state:
    { selectedTicket: SelectedTicketState }) => state.selectedTicket);
  const selectedTicket: Ticket = selectedTicketDetails?.ticket;
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const selectedBuilding: Building | null = (buildings && selectedBuildingId)
    ? buildings[selectedBuildingId] : null;
  const filterState: TicketsFilterState = useSelector((state:
    { ticketsFilter: TicketsFilterState }) => state.ticketsFilter);
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);
  const [clientName, setClientName] = useState<string>(null);
  const [tickets, setTickets] = useState<Ticket[]>(null);
  const [pinnedTickets, setPinnedTickets] = useState<Ticket[]>(null);
  const [ticketCount, setTicketCount] = useState<number | undefined>(undefined);
  const [counts, setCounts] = useState<OpenTicketStatistics>(null);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [imageModalVisible, setImageModalVisible] = useState<boolean>(false);
  const [moreOptionsVisible, setMoreOptionsVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [newReports, setNewReports] = useState<boolean>(false);
  // eslint-disable-next-line no-unused-vars -- use for skeleton loading
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [shareDropdownShown, setShareDropdownShown] = useState<boolean>(false);
  const [shareConfirmationShown, setShareConfirmationShown] = useState<boolean>(false);
  const [otherExpanded, setOtherExpanded] = useState<boolean>(false);
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [userGroups, setUserGroups] = useState([]);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertAction, setAlertAction] = useState(null);
  const [alertActionTitle, setAlertActionTitle] = useState(null);
  const [alertCloseTitle, setAlertCloseTitle] = useState(null);
  const [screenAlertVisible, setScreenAlertVisible] = useState<boolean>(false);
  const [screenAlertError, setScreenAlertError] = useState<boolean>(true);
  const [screenAlertTitle, setScreenAlertTitle] = useState<string>('');
  const [screenAlertMessage, setScreenAlertMessage] = useState<string | ReactElement>(null);
  const [screenAlertAction, setScreenAlertAction] = useState<() => void>(null);
  const [screenAlertActionTitle, setScreenAlertActionTitle] = useState<string>(null);
  const [screenAlertCloseTitle, setScreenAlertCloseTitle] = useState<string>(null);
  const [selectedRange, setSelectedRange] = useState<DateSelectionRange>(1);
  const [isDev, setIsDev] = useState<boolean>(false);
  const [welcomeVisible, setWelcomeVisible] = useState<boolean>(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [newReportTime, setNewReportTime] = useState<boolean>(false);
  const [saveFilterToggled, setSaveFilterToggled] = useState<boolean>(false);
  const [savedFiltersLoaded, setSavedFiltersLoaded] = useState<boolean>(false);
  const [closureModalVisible, setClosureModalVisible] = useState<boolean>(false);
  const [openTicketsSelection, setOpenTicketsSelection] = useState<OpenTicketsShown | null>(null);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const limit: number = 15;
  const [offset, setOffset] = useState<number>(0);
  const [otherPriorityFilter, setOtherPriorityFilter] = useState<boolean>(false);
  const [lowPriorityFilter, setLowPriorityFilter] = useState<boolean>(false);
  const [mediumPriorityFilter, setMediumPriorityFilter] = useState<boolean>(false);
  const [highPriorityFilter, setHighPriorityFilter] = useState<boolean>(false);
  const [criticalPriorityFilter, setCriticalPriorityFilter] = useState<boolean>(false);
  const [doneFilter, setDoneFilter] = useState<boolean>(false);
  const [inProgressFilter, setInProgressFilter] = useState<boolean>(false);
  const [notStartedFilter, setNotStartedFilter] = useState<boolean>(false);
  const [slaOverdueFilter, setSlaOverdueFilter] = useState<boolean>(false);
  const [slaSort, setSlaSort] = useState<string>('');
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [selectedAssigneeName, setSelectedAssigneeName] = useState<string>('');
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const dispatch = useDispatch();
  const setSelectedTicket = (ticket: Ticket | null) => dispatch(updateSelectedTicket(ticket));
  const ticketActivityRef = useRef(null);
  const ticketCountWidgetRef = useRef(null);
  const emitter: NativeEventEmitter = Platform.OS === 'web' ? new NativeEventEmitter() : null;
  const scrollView: Ref<ScrollView> = useRef<ScrollView>(null);
  const flatList: Ref<FlatList> = useRef<FlatList>(null);
  const slaNames: string[] = useSelector((state: {
    selectedBuilding: { SlaHours: BuildingSettingsPriority[] }
  }) => state
    .selectedBuilding.SlaHours?.map((item: BuildingSettingsPriority) => item.Name), shallowEqual);
  const originalFetchTime = Moment();
  const {
    firstName, lastName, isAdmin, permissions,
  } = useSelector((state:
    {
      user: {
        firstName: string, lastName: string, isAdmin: boolean, permissions: UserPermission[],
      }
    }) => state.user);
  const canViewTickets: boolean = isAdmin
    || permissions.includes(UserPermission.TICKETS_READ);

  const translateX = useSharedValue(0);

  const isFocused = useIsFocused();

  const loadError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? TICKET_LIST_LOAD_FAILURE,
    true,
  );

  const showAlert = (
    title: string,
    message?: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
    closeTitle: string = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertCloseTitle(closeTitle);
    setAlertVisible(true);
  };

  const showScreenAlert = (
    title: string,
    message?: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
    closeTitle: string = null,
  ) => {
    setScreenAlertTitle(title);
    setScreenAlertMessage(message);
    setScreenAlertError(error);
    setScreenAlertAction(() => action);
    setScreenAlertActionTitle(actionTitle);
    setScreenAlertCloseTitle(closeTitle);
    setLoading(false);
    setScreenAlertVisible(true);
  };

  const loadSavedData: () => void = async () => {
    AsyncStorage.getItem('email').then(updateEmail);
    AsyncStorage.getItem('clientName').then(setClientName);
    AsyncStorage.getItem('environment').then((env) => setIsDev(env === 'dev' || env === 'demo'));
    await AsyncStorage.getItem('saveFilterToggled').then((val) => {
      const saveFiltersEnabled: boolean = val === 'yes';
      setSaveFilterToggled(saveFiltersEnabled);
      if (saveFiltersEnabled) {
        setOtherPriorityFilter(filterState.otherPriority);
        setLowPriorityFilter(filterState.lowPriority);
        setMediumPriorityFilter(filterState.mediumPriority);
        setHighPriorityFilter(filterState.highPriority);
        setCriticalPriorityFilter(filterState.criticalPriority);
        setDoneFilter(filterState.doneFilter);
        setInProgressFilter(filterState.inProgressFilter);
        setNotStartedFilter(filterState.notStartedFilter);
        setSlaOverdueFilter(filterState.slaOverdueFilter);
        setSelectedAssetId(filterState.selectedAssetId);
        setSelectedUserGroupId(filterState.selectedUserGroupId);
        setSelectedAssignee(filterState.selectedAssignee);
        setSelectedLocation(filterState.selectedLocation);
        setSlaSort(filterState.slaSort);
        if (filterState.selectedDateRange?.start && filterState.selectedDateRange?.end) {
          setRange(filterState.selectedDateRange);
        }
      } else resetAllFilters();
    });
    setSavedFiltersLoaded(true);
  };

  const bottomSafeAreaInset: number = useSafeAreaInsets().bottom;

  const [range, setRange] = useState<DateRange>({ start: null, end: null });

  const onConfirm = useCallback((start, end) => setRange({ start, end }), [setRange]);

  const handleTap: () => void = () => {
    if (modalVisible) setModalVisible(false);
    flatList?.current?.scrollToOffset({ animated: true, offset: 0 });
  };

  const loadData: () => void = () => {
    getMyUser().then((result) => {
      AsyncStorage.getItem('happyEnabled').then((val) => {
        if (val !== 'enabled') {
          if (result.firstName?.trim().length < 1
            || result.lastName?.trim().length < 1) setWelcomeVisible(true);
        }
      });
      if (!result || !result?.clientId) return;
      const userFirstName: string | undefined = result.firstName?.trim();
      const userLastName: string | undefined = result.lastName?.trim();
      if (userFirstName && userLastName) {
        dispatch(updateUsername({ firstName: userFirstName, lastName: userLastName }));
      }
    });
    getAssets({ locationId: selectedBuildingId, enableSubLocationsLookUp: true }).then((result) => {
      if (result?.assets?.length > 0) {
        setAssets(result.assets.sort((a, b) => a?.Name?.localeCompare(b.Name)));
      }
    });
    getUserGroups().then((response) => {
      if (response?.UserGroups?.length > 0) setUserGroups(response.UserGroups);
    });
  };

  const updatePinnedTickets = (updatedTickets: Ticket[]) => {
    if (updatedTickets?.length > 3) updatedTickets.shift();
    setPinnedTickets(updatedTickets);
  };

  const [email, updateEmail] = useState<string>('');
  const [myClientIdEmail, setMyClientIdEmail] = useState<string>('');

  const name: string = `${firstName ?? ''} ${lastName ?? ''}`;

  const closeMenus: () => void = () => {
    setShareDropdownShown(false);
    setShareConfirmationShown(false);
  };

  useEffect(() => {
    loadSavedData();
    navigation.addListener('tabPress', handleTap);
    emitter?.addListener('event.newupdates', (params) => {
      setNewReportTime(params.timestamp);
    });
    loadData();
    const unsubscribe = navigation.addListener('blur', () => {
      setFilterOpen(false);
    });
    if (Platform.OS === 'android') StatusBar.setBarStyle('dark-content');
    return unsubscribe;
  }, []);

  // TODO: Remove with Expo SDK 54 upgrade
  useEffect(() => {
    const tokenListener = Notifications.addPushTokenListener(() => {
      // console.log(token);
    });

    return () => {
      if (tokenListener) {
        tokenListener.remove();
      }
    };
  }, []);

  useEffect(() => {
    getPushNotificationsToken();
    if (Platform.OS !== 'web') {
      AsyncStorage.getItem('happyEnabled').then((val) => {
        if (val !== 'enabled') {
          getSupportedVersions().then((res) => {
            if (res?.details) {
              if (Platform.OS === 'ios') {
                if (Application.nativeBuildVersion < res.details?.ios?.latestVersion) {
                  setUpdateModalVisible(true);
                }
              } else if (Application.nativeBuildVersion < res.details?.android?.latestVersion) {
                setUpdateModalVisible(true);
              }
            }
          });
        }
      });
    }
    const unsubscribe = navigation.addListener('focus', () => {
      loadData();
      closeMenus();
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!selectedTicket || !isFocused) return () => { };

    if (selectedTicket.buildingId !== selectedBuildingId) {
      dispatch(updateBuildingId(selectedTicket.buildingId));
      return () => { };
    }

    setSelectedTicket(selectedTicket);
    if (selectedTicket?.pinned
      && !pinnedTickets?.map((t: Ticket) => t.id).includes(selectedTicket.id)) {
      updatePinnedTickets([...(pinnedTickets ?? []), selectedTicket]);
    }

    const timeout = setTimeout(() => {
      // dispatch(updateSelectedTicket(undefined));
      setModalVisible(true);
    }, 300); // Slightly longer delay to allow screen transition to settle

    return () => clearTimeout(timeout);
  }, [selectedTicket, isFocused]);

  useEffect(() => {
    if (newReportTime > lastFetchTime) {
      setNewReports(true);
    }
  }, [newReportTime]);

  useEffect(() => {
    if (email?.length > 0 && clientName?.length > 0 && !(myClientIdEmail?.length > 0)) {
      setMyClientIdEmail(`${clientName}-${email}`);
    }
  }, [clientName, email]);

  useEffect(() => {
    const filterDateRange: DateRange = (route?.params as any)?.filterDateRange;
    if (filterDateRange) {
      setRange(filterDateRange);
      navigation?.setParams({ filterDateRange: undefined } as any);
    }
    const assetId = (route.params as any)?.assetId;
    const selectedPeriod = (route.params as any)?.selectedTimePeriod;
    if (assetId) {
      dispatch(updateTicketsFilterAssetId(assetId ?? ''));
      navigation.setParams({ assetId: undefined } as any);
    }

    if (selectedPeriod) {
      if (selectedPeriod === 'day') {
        setRange({
          start: Moment().startOf('day').toDate().toISOString(),
          end: Moment().endOf('day').toISOString(),
        });
      } else if (selectedPeriod === 'week') {
        setRange({
          start: Moment().subtract(6, 'days').startOf('day').toDate()
            .toISOString(),
          end: Moment().endOf('day').toISOString(),
        });
      } else if (selectedPeriod === 'month') {
        setRange({
          start: Moment().subtract(1, 'month').startOf('day').toDate()
            .toISOString(),
          end: Moment().endOf('day').toISOString(),
        });
      } else if (selectedPeriod === 'year') {
        setRange({
          start: Moment().subtract(1, 'year').startOf('day').toDate()
            .toISOString(),
          end: Moment().endOf('day').toISOString(),
        });
      }
      setTickets([]);
      setTicketCount(undefined);
      setLoading(true);
    }
  }, [route]);

  useEffect(() => {
    if (!clientName) return;
    const { clientId, reportId, buildingId } = (route.params as any) ?? {};
    if (!reportId) return;
    if (!clientId) showAlert('Error', 'Client ID not specified for this ticket');
    else if (clientId !== clientName) showAlert('Error', 'Ticket belongs to a different client');
    else if (!buildingId) showAlert('Error', 'Building ID not specified for this ticket');
    else if (!reportId) showAlert('Error', 'Ticket ID not specified for this ticket');
    else {
      getTicket(reportId).then((res) => {
        if (res) {
          dispatch(updateBuildingId(buildingId));
          setSelectedTicket(res);
        } else showAlert('Error', 'Failed to load ticket');
      }).catch(() => showAlert('Error', 'Failed to load ticket'));
    }
  }, [route, clientName]);

  useEffect(() => {
    if (loading && selectedBuildingId && savedFiltersLoaded) {
      setShowSpinner(true);
      getTickets({
        other: otherPriorityFilter,
        low: lowPriorityFilter,
        moderate: mediumPriorityFilter,
        high: highPriorityFilter,
        critical: criticalPriorityFilter,
        done: !openTicketsSelection && doneFilter,
        inprogress: !!openTicketsSelection || inProgressFilter,
        reported: !!openTicketsSelection || notStartedFilter,
        assignee: (openTicketsSelection === OpenTicketsShown.OPEN_ASSIGNED_TO_ME
          || openTicketsSelection === OpenTicketsShown.OPEN_ASSIGNED_TO_ME_OVERDUE
        ) ? myClientIdEmail : selectedAssignee,
        buildingId: selectedBuildingId,
        assetId: selectedAssetId,
        usergroupIds: [selectedUserGroupId],
        startTime: range.start,
        endTime: range.end,
        locationId: selectedLocation,
        overdue: openTicketsSelection === OpenTicketsShown.OPEN_OVERDUE
          || openTicketsSelection === OpenTicketsShown.OPEN_ASSIGNED_TO_ME_OVERDUE
          || slaOverdueFilter ? true : null,
        limit,
        offset,
        textSearch: searchValue,
      }).then(async (response) => {
        if (response) {
          const newTickets: Ticket[] = response.tickets ?? [];
          if (slaSort === 'ascending') {
            newTickets.sort((a, b) => a.slaHours * 60 + a.slaMinutes
              - b.slaHours * 60 - b.slaMinutes);
          } else if (slaSort === 'descending') {
            newTickets.sort((a, b) => b.slaHours * 60 + b.slaMinutes
              - a.slaHours * 60 - a.slaMinutes);
          }
          setTickets(newTickets);
          setTicketCount(response.count ?? response.statistics?.totalTickets);
        } else loadError();
        setLoading(false);
        setNewReports(false);
        setShowSpinner(false);
        setLastFetchTime(Moment());
      }).catch((error) => loadError(error?.message));
    } else setShowSpinner(false);
  }, [loading, selectedBuildingId, savedFiltersLoaded]);

  useEffect(() => setLoading(true), [
    otherPriorityFilter, lowPriorityFilter, mediumPriorityFilter, highPriorityFilter,
    criticalPriorityFilter, doneFilter, inProgressFilter, notStartedFilter, slaOverdueFilter,
    selectedAssetId, selectedAssignee, selectedBuildingId, selectedUserGroupId, selectedLocation,
    slaSort, range, openTicketsSelection, searchValue,
  ]);

  useEffect(closeMenus, [selectedTicket]);

  useEffect(() => {
    getUsers().then((response) => {
      if (response?.items?.length > 0) {
        setUsers(response.items
          .filter((user: UserInstance) => user.status === UserStatus.VERIFIED)
          .sort((prev: UserInstance, next: UserInstance) => (prev.firstName ?? prev.lastName
            ?? prev.email).toLowerCase().localeCompare((next.firstName ?? next.lastName
              ?? next.email).toLowerCase())));
        response.items.forEach((user) => {
          if (user.id === email) setMyClientIdEmail(user.id);
        });
      }
    });
  }, [email]);

  useEffect(() => setSelectedAssetId(filterState.selectedAssetId), [filterState.selectedAssetId]);

  useEffect(() => {
    if (!modalVisible) {
      translateX.value = 0;
    }

    const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      if (modalVisible) {
        setModalVisible(false);
        return true;
      }
      return false;
    });

    return () => backHandler.remove();
  }, [modalVisible]);

  useEffect(() => {
    updatePinnedTickets(pinnedTickets
      ?.filter((ticket: Ticket) => ticket.buildingId === selectedBuildingId));
    if (selectedTicket?.buildingId !== selectedBuildingId) setSelectedTicket(null);
  }, [selectedBuildingId]);

  const loadTickets = (pageNumber: number) => {
    setOffset(pageNumber * limit);
    setLoading(true);
  };

  const updateSelectedTicketInList = (updatedTicket: Ticket) => {
    setSelectedTicket(updatedTicket);
    setTickets(tickets.map((t: Ticket) => (t.id === updatedTicket.id ? updatedTicket : t)));
    updatePinnedTickets(pinnedTickets
      ?.map((t: Ticket) => (t.id === updatedTicket.id ? updatedTicket : t)));
    ticketCountWidgetRef?.current?.refresh();
    ticketActivityRef?.current?.refreshActivity();
  };

  const resetAllFilters: () => void = () => {
    dispatch(resetAllTicketFilters());
    setOtherPriorityFilter(false);
    setLowPriorityFilter(false);
    setMediumPriorityFilter(false);
    setHighPriorityFilter(false);
    setCriticalPriorityFilter(false);
    setDoneFilter(false);
    setInProgressFilter(false);
    setNotStartedFilter(false);
    setSlaOverdueFilter(false);
    setSelectedAssetId('');
    setSelectedUserGroupId('');
    setSelectedAssignee('');
    setSelectedLocation('');
    setRange({ start: null, end: null });
    setLoading(true);
  };

  const renderSharePane = () => <TicketSharePane
    {...{
      isDev,
      selectedBuilding: selectedBuilding?.LocationId,
      selectedTicket,
      shareConfirmationShown,
      setShareConfirmationShown,
      shareDropdownShown,
      setShareDropdownShown,
      showAlert,
      deleteTicket: attemptDeleteTicket,
      clientName,
    }}
  />;

  const renderFilter = () => <TicketsFilter
    {...{
      onConfirm,
      range,
      selectedRange,
      setSelectedRange,
      users,
      assets,
      setLoading,
      setFilterOpen,
      setTickets,
      setSelectedTicket,
      userGroups,
      otherPriorityFilter,
      setOtherPriorityFilter,
      lowPriorityFilter,
      setLowPriorityFilter,
      mediumPriorityFilter,
      setMediumPriorityFilter,
      highPriorityFilter,
      setHighPriorityFilter,
      criticalPriorityFilter,
      setCriticalPriorityFilter,
      doneFilter,
      setDoneFilter,
      inProgressFilter,
      setInProgressFilter,
      notStartedFilter,
      setNotStartedFilter,
      slaOverdueFilter,
      setSlaOverdueFilter,
      slaSort,
      setSlaSort,
      selectedAssignee,
      setSelectedAssignee,
      selectedAssigneeName,
      setSelectedAssigneeName,
      selectedAssetId,
      setSelectedAssetId,
      selectedUserGroupId,
      setSelectedUserGroupId,
      selectedLocation,
      setSelectedLocation,
      setSelectedFloor: () => { },
      tickets,
      filterShown: filterOpen,
      saveFilterToggled,
      setSaveFilterToggled,
      slaNames,
      resetAllFilters,
    }}
  />;

  const renderMobileFilter = () => <Modal
    isVisible
    style={[Style.modal, styles.mobileFilterContainer]}
    animationIn={'fadeIn'}
    animationOut={'fadeOut'}
    onBackButtonPress={() => {
      logEvent('Tickets', 'filter_modal_back');
      setFilterOpen(false);
    }}
    statusBarTranslucent
    backdropColor={Colors.light.hereworksPrimaryGrey}
    backdropOpacity={0.40}
  >
    <ScrollView
      style={{
        flex: 1,
        marginTop: 16,
        paddingBottom: bottomSafeAreaInset,
      }}
    >
      <View style={{ flexDirection: 'row', justifyContent: 'center', marginHorizontal: 24 }}>
        <PressableStyled
          onPress={() => {
            logEvent('Tickets', 'reset_filters');
            resetAllFilters();
            setFilterOpen(false);
          }}
        >
          <Text style={styles.resetTitle}>{'Reset'}</Text>
        </PressableStyled>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={styles.filterTitle}>{'Filter'}</Text>
        </View>
        <PressableStyled
          onPress={() => {
            logEvent('Tickets', 'filter_close_button');
            setFilterOpen(false);
          }}
        >
          <CloseModalIcon />
        </PressableStyled>
      </View>
      {renderFilter()}
    </ScrollView>
  </Modal>;

  const FilterButton = () => <Button
    title='Filters'
    children={<FilterIcon16 style={styles.filterIcon} />}
    onPress={() => {
      logEvent('Tickets', 'filter_button');
      setFilterOpen(!filterOpen);
    }}
    testID='tickets-filter-btn'
    style={styles.filterButton}
  />;

  const renderWebTickets = () => <View style={styles.webTickets}>
    <View style={Style.row}>
      <SearchBar
        apply={setSearchValue}
        {...{ loading }}
        placeholder='Search tickets by name or ID'
      />
      <FilterButton />
    </View>
    {ticketsList()}
  </View>;

  const renderMobileTickets = () => <View style={{ flex: 1, marginHorizontal: 18 }}>
    {Device.deviceType === Device.DeviceType.TABLET ? null : <MobileFilterButton />}
    {showSearch ? <View style={styles.searchBoxMobile}>
      <SearchIcon />
      <TextInput
        placeholder='Search tickets'
        placeholderTextColor={Colors.light.hereworksPrimaryGrey700}
        style={styles.searchText}
        onChangeText={setSearchValue}
      />
    </View> : <></>}
    {ticketsList()}
  </View>;

  const MobileFilterButton = ({ tablet = false }) => (
    <View style={[tablet ? {} : { alignSelf: 'flex-end', marginRight: 0 }]}>
      <View style={{ flexDirection: 'row', marginTop: 8 }}>
        <View style={{
          flexDirection: 'row', zIndex: 2, justifyContent: 'flex-end', alignItems: 'center',
        }}>
          <PressableStyled
            style={{
              ...styles.filterButton, marginRight: 8, paddingHorizontal: 4, paddingVertical: 4,
            }}
            onPress={() => {
              logEvent('Tickets', 'show_search');
              setSearchValue('');
              setShowSearch(!showSearch);
            }}>
            <SearchIcon />
          </PressableStyled>
          <FilterButton />
        </View>
        {filterOpen && renderMobileFilter()}
      </View>
    </View>
  );

  const removeTicket = (deletedTicketId: string) => {
    setSelectedTicket(null);
    setTickets(tickets.filter((ticket: Ticket) => ticket.id !== deletedTicketId));
    updatePinnedTickets(pinnedTickets
      ?.filter((ticket: Ticket) => ticket.id !== deletedTicketId));
    setTicketCount(typeof ticketCount === 'number' ? ticketCount - 1 : undefined);
  };

  const attemptDeleteTicket = () => {
    const deletedTicketName: string = selectedTicket.name;
    logEvent('Tickets', 'delete_ticket_confirm');
    setShowSpinner(true);
    const deletedTicketId = selectedTicket.id;
    deleteTicket(selectedTicket.id).then((response) => {
      if (response && !response.isError && !response.errorText) {
        if (isMobile) {
          setModalVisible(false);
          showScreenAlert(
            DELETE_TICKET_SUCCESS,
            <Text numberOfLines={2}>
              Ticket{' '}
              <Text style={Style.alertBoldText} numberOfLines={1}>
                {`'${deletedTicketName}'`}
              </Text>{' '}
              - successfully deleted
            </Text>,
            false,
            null,
            null,
            'Ok',
          );
          removeTicket(deletedTicketId);
          setLoading(true);
        } else {
          removeTicket(deletedTicketId);
          showAlert(
            DELETE_TICKET_SUCCESS,
            <Text numberOfLines={2}>
              Ticket{' '}
              <Text style={Style.alertBoldText} numberOfLines={1}>
                {`'${deletedTicketName}'`}
              </Text>{' '}
              - successfully deleted
            </Text>,
            false,
            null,
            'Ok',
          );
        }
      } else showAlert('Error', response?.errorText);
      emitter?.emit('event.updatenotifications', {});
    }).finally(() => setShowSpinner(false));
  };

  const resolve: () => void = () => {
    const updatedTicket = {
      ...selectedTicket,
      status: TicketStatus.COMPLETED,
      completedOn: Moment().toString(),
    };
    updateSelectedTicketInList(updatedTicket);
    setLoading(true);
    emitter?.emit('event.updatenotifications', {});
  };

  const renderMoreOptions = () => <TicketOptions
    {...{
      moreOptionsVisible,
      setMoreOptionsVisible,
      selectedTicket,
      showAlert,
      deleteTicket: attemptDeleteTicket,
    }}
  />;

  const panStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
    ],
  }), []);

  const renderModal = () => (
    <Modal
      isVisible={modalVisible}
      style={[styles.fullScreenModal]}
      animationIn={'slideInRight'}
      animationOut={'slideOutRight'}
      animationInTiming={200}
      animationOutTiming={200}
      backdropOpacity={0}
      onBackButtonPress={() => {
        logEvent('Tickets', 'close_modal');
        dispatch(updateSelectedTicket(null));
        setModalVisible(false);
      }}
      onBackdropPress={() => setModalVisible(false)}
      onSwipeComplete={() => {
        logEvent('Tickets', 'close_modal');
        dispatch(updateSelectedTicket(null));
        setModalVisible(false);
      }}
      statusBarTranslucent
      // useNativeDriverForBackdrop
      // useNativeDriver
      // hideModalContentWhileAnimating
      coverScreen={false}
      swipeDirection={['right']}
      propagateSwipe={true}
    >
      <Animated.View style={[styles.modal, panStyle]}>
        <View style={styles.modalHeaderContainer}>
          <PressableStyled
            onPress={() => {
              logEvent('Tickets', 'close_modal');
              dispatch(updateSelectedTicket(null));
              setModalVisible(false);
            }}
            style={{ marginRight: 10 }}
            hitSlop={{
              top: 30,
              bottom: 40,
              left: 50,
              right: 50,
            }}
          >
            <LeftArrow />
          </PressableStyled>
          <Text style={styles.modalticketTitle}>
            {selectedTicket?.name?.length > 50 ? 'Other' : selectedTicket?.name}
          </Text>
          <Modal
            isVisible={moreOptionsVisible}
            style={Style.modal}
            hasBackdrop={false}
            animationIn={'fadeIn'}
            animationOut={'fadeOut'}
            onBackButtonPress={() => {
              logEvent('Tickets', 'more_options_back');
              setMoreOptionsVisible(false);
            }}
          >
            <PressableStyled
              style={{ flex: 1 }}
              onPress={() => {
                logEvent('Tickets', 'more_options');
                setMoreOptionsVisible(!moreOptionsVisible);
              }}
            >
              {renderMoreOptions()}
            </PressableStyled>
          </Modal>
        </View>
        {renderSelectedTicket()}
        <AlertModal
          modalVisible={alertVisible}
          title={alertTitle}
          message={alertMessage}
          error={alertError}
          action={alertAction}
          actionTitle={alertActionTitle}
          closeTitle={alertCloseTitle}
          setModalVisible={setAlertVisible}
        />
      </Animated.View>
    </Modal>
  );

  const count: number = (openTicketsSelection && counts)
    ? counts[openTicketsSelection] : ticketCount;

  const ticketsList = () => ((selectedBuilding && buildings) ? <TicketList
    {...{
      newReports,
      showSpinner,
      loading,
      setLoading,
      setRefreshing,
      tickets,
      pinnedTickets,
      updatePinnedTickets,
      flatList,
      setModalVisible,
      selectedTicket,
      setSelectedTicket,
      originalFetchTime,
      count,
      loadTickets,
    }}
    // loading={true} // FOR TESTING
    // tickets={[]} // FOR TESTING
    listSize={limit}
    renderFilterTags={() => <TicketFilterTagSection
      {...{
        otherPriorityFilter,
        lowPriorityFilter,
        mediumPriorityFilter,
        highPriorityFilter,
        criticalPriorityFilter,
        doneFilter,
        inProgressFilter,
        notStartedFilter,
        slaSort,
        selectedAssignee,
        selectedUserGroupId,
        selectedAssetId,
        selectedLocation,
        slaOverdueFilter,
        setTickets,
        range,
        users,
        userGroups,
        setLoading,
        resetAllFilters,
        slaNames,
        setOtherPriorityFilter,
        setLowPriorityFilter,
        setMediumPriorityFilter,
        setHighPriorityFilter,
        setCriticalPriorityFilter,
        setDoneFilter,
        setInProgressFilter,
        setNotStartedFilter,
        setSlaOverdueFilter,
        setSlaSort,
        setSelectedAssignee,
        setSelectedUserGroupId,
        setSelectedAssetId,
        setSelectedLocation,
        setRange,
      }}
      selectedAssetName={assetName(selectedAssetId, assets)}
    />}
  /> : null);

  const renderSelectedTicket = () => <SelectedTicket
    {...{
      showAlert,
      users,
      selectedTicket,
      renderSharePane,
      renderMoreOptions,
      moreOptionsVisible,
      selectedBuilding: selectedBuilding?.LocationId,
      imageModalVisible,
      setImageModalVisible,
      ticketActivityRef,
      otherExpanded,
      setOtherExpanded,
      scrollView,
      showSpinner,
      setModalVisible,
      name,
      closureModalVisible,
      setClosureModalVisible,
      resolve,
      setLoading,
      setShowSpinner,
      userGroups,
      updateSelectedTicketInList,
    }}
    asset={assets?.find((a: AssetInstance) => a.id === selectedTicket?.assetId) ?? null}
    hide={() => setModalVisible(false)}
  />;

  const content = () => {
    if (!notEmpty) {
      return <ScreenEmptyState
        image={selectedBuilding ? <NoTicketsImage /> : <NoBuildingsIconMobile />}
        title={buildings ? null : 'Loading building...'}
        subtitle={buildings ? undefined : null}
        large
      />;
    }
    return canViewTickets ? <View style={styles.wrapper}>
      <View style={styles.header}>
        {isMobile ? <Text style={styles.title}>{'Tickets'}</Text> : <></>}
        <Text style={styles.welcomeText}>{`Hi ${firstName} 👋`}</Text>
      </View>
      <TicketCountWidget
        {...{
          openTicketsSelection, setOpenTicketsSelection, MobileFilterButton, counts, setCounts,
        }}
        ref={ticketCountWidgetRef}
      />
      <View style={styles.container}>
        <View style={styles.ticketList}>
          {isMobile ? renderMobileTickets() : renderWebTickets()}
          <WelcomeModal
            isVisible={welcomeVisible}
            onPress={setWelcomeVisible}
          />
        </View>
        {!isMobile && <View style={styles.selectedTicketContainerWeb}>
          {!!selectedBuilding && tickets?.length > 0 && renderSelectedTicket()}
        </View>}
        {isMobile ? <AlertModal
          modalVisible={screenAlertVisible}
          title={screenAlertTitle}
          message={screenAlertMessage}
          error={screenAlertError}
          action={screenAlertAction}
          actionTitle={screenAlertActionTitle}
          closeTitle={screenAlertCloseTitle}
          setModalVisible={setScreenAlertVisible}
        /> : <AlertModal
          modalVisible={alertVisible}
          title={alertTitle}
          message={alertMessage}
          error={alertError}
          action={alertAction}
          actionTitle={alertActionTitle}
          closeTitle={alertCloseTitle}
          setModalVisible={setAlertVisible}
        />}
      </View>
      {(!isMobile && filterOpen) && renderFilter()}
      {isMobile ? <UpdateAppModal
        isVisible={updateModalVisible}
        hide={() => setUpdateModalVisible(false)} /> : null}
      {isMobile && renderModal()}
    </View> : <ScreenEmptyState
      title={noPermissionsTitle('tickets')}
      subtitle={noPermissionsSubtitle('tickets')}
      large
      image={<NoTicketsImage />}
    />;
  };

  return Platform.OS === 'web' ? <Pressable
    onPress={closeMenus}
    style={{ flex: 1, cursor: 'auto' }}
  >
    {content()}
  </Pressable> : content();
}

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
    overflow: 'hidden',
  },
  header: {
    marginHorizontal: 24,
    marginTop: Platform.OS === 'android' ? undefined : 24,
  },
  container: {
    flex: 1,
    paddingHorizontal: isMobile ? 0 : 24,
    flexDirection: 'row',
  },
  searchBoxMobile: {
    height: 40,
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksBackground,
    marginTop: 13,
    borderColor: Colors.light.hereworksPaleBlue900,
    borderWidth: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: -1,
    paddingHorizontal: 12,
  },
  searchText: {
    color: Colors.light.hereworksPrimaryGrey,
    marginLeft: 9,
    outlineWidth: 0,
  },
  modalticketTitle: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 18,
    color: 'black',
    marginLeft: 14,
    marginRight: 14,
    textAlign: 'center',
    flex: 1,
  },
  title: {
    fontSize: isMobile ? 22 : 32,
    fontFamily: isMobile ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
    marginBottom: isMobile ? 8 : 32,
    lineHeight: 33,
    color: Colors.light.hereworksBlack600,
    paddingTop: isMobile ? 32 : 14,
  },
  welcomeText: {
    fontSize: 22,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  modalHeaderContainer: {
    flexDirection: 'row',
    height: 50,
    marginTop: 50,
    alignItems: 'center',
    marginHorizontal: 20,
    backgroundColor: Colors.light.hereworksBackground,
  },
  filterButton: {
    flex: undefined,
    paddingHorizontal: 24,
    marginStart: 12,
  },
  filterIcon: {
    marginStart: 8,
  },
  mobileFilterContainer: {
    backgroundColor: '#FFF',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    marginTop: 70,
    maxWidth: 450,
    height: '90%',
    position: 'absolute',
    bottom: 0,
    alignSelf: 'center',
    justifyContent: 'flex-end',
  },
  filterTitle: {
    marginLeft: 24,
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
  },
  resetTitle: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    color: Colors.light.hereworksBlack600,
  },
  ticketList: {
    flex: 1,
    zIndex: 1,
  },
  modal: {
    flex: 1,
    margin: 0,
    backgroundColor: Colors.light.hereworksBackground,
  },
  webTickets: {
    marginBottom: 18,
    marginEnd: 12,
    backgroundColor: '#fff',
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderColor: Colors.light.hereworksWhite300,
    borderWidth: 1,
    borderRadius: 24,
    flex: 1,
  },
  selectedTicketContainerWeb: {
    flex: 1,
    marginLeft: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    borderRadius: 24,
    marginBottom: 18,
    overflow: 'hidden',
  },
  fullScreenModal: {
    margin: 0,
    justifyContent: 'flex-end',
    zIndex: 999,
  },
});
